/**
 * Submitting evidence for one assignment.
 *
 * The page shows exactly what the competency asks for and nothing else:
 * the attestation to confirm, the file to attach, a place for notes. Files
 * go to Storage under the person's own path, and the record keeps the
 * URLs. If a manager must approve, the page says so on the way out.
 */

import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { isCallFailure } from '../../lib/callable';
import { COMP_ASSESSOR_MAX, COMP_FILES_MAX, COMP_SCORE_MAX, COMP_TEXT_MAX } from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { FileDrop } from '../../shared/FileDrop';
import { formatBytes } from '../policies/useUpload';
import { formatDay } from '../../shared/dates';
import { useCompetencyDetail, useMyAssignments, useSubmitEvidence } from './useCompetencies';
import { EVIDENCE_ACCEPT, evidenceFileProblem, uploadEvidenceFile } from './uploadEvidence';
import { assignmentActions, DEFAULT_ATTESTATION } from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Competencies.module.css';

export const SUBMITTED_KEY = 'somexai.competency.submitted';

export function EvidencePage() {
  const { assignmentId = '' } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const roles = session?.roles ?? [];

  const mine = useMyAssignments(orgId, uid, true);
  const assignment = (mine.data ?? []).find((a) => a.id === assignmentId) ?? null;
  const detail = useCompetencyDetail(orgId, assignment?.competencyId ?? null);
  const submitEvidence = useSubmitEvidence(orgId, uid);

  const [attested, setAttested] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [assessorName, setAssessorName] = useState('');
  const [assessmentDate, setAssessmentDate] = useState('');
  const [score, setScore] = useState('');
  const [uploading, setUploading] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  if (mine.isLoading || (assignment && detail.isLoading)) return <Spinner label="Opening the assignment" />;
  if (!assignment) {
    return (
      <div className={styles.body}>
        <PageState title="That assignment is not yours, or is not here" action={{ to: '/competencies/mine', label: 'Back to your assignments' }} tone="alert">
          Only the person it was assigned to can submit evidence for it.
        </PageState>
      </div>
    );
  }
  const can = assignmentActions(assignment, roles, uid);
  if (!can.submit) {
    return (
      <div className={styles.body}>
        <PageState title={can.awaitingApproval ? 'Evidence already submitted' : 'Nothing to submit'} action={{ to: '/competencies/mine', label: 'Back to your assignments' }}>
          {can.awaitingApproval ? 'It is waiting on a manager\'s approval.' : `This assignment is ${assignment.status}.`}
        </PageState>
      </div>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <div className={styles.body}>
        <PageState title="Could not open the competency" action={{ to: '/competencies/mine', label: 'Back to your assignments' }} tone="alert">
          {isCallFailure(detail.error) ? detail.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }

  const c = detail.data.competency;
  const req = c.evidenceRequirements;
  const statement = req.attestationText ?? DEFAULT_ATTESTATION;
  const needAttest = req.attestation && !attested;
  const needFile = req.fileUpload && files.length === 0;
  const busy = submitEvidence.isPending || uploading !== null;

  function addFiles(chosen: File[]) {
    setProblem(null);
    const next = [...files];
    for (const f of chosen) {
      const why = evidenceFileProblem(f);
      if (why) { setProblem(why); continue; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      if (next.length >= COMP_FILES_MAX) { setProblem(`Up to ${COMP_FILES_MAX} files.`); break; }
      next.push(f);
    }
    setFiles(next);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setFailure(null);
    if (needAttest || needFile) return;
    try {
      const urls: string[] = [];
      for (const f of files) {
        setUploading(f.name);
        urls.push(await uploadEvidenceFile(orgId!, uid!, assignment!.id, f));
      }
      setUploading(null);
      const res = await submitEvidence.mutateAsync({
        assignmentId: assignment!.id,
        attestationText: req.attestation ? statement : '',
        fileUrls: urls,
        notes,
        assessorName,
        assessmentDate,
        score,
      });
      try {
        window.sessionStorage.setItem(SUBMITTED_KEY, res.needsManagerApproval
          ? `Evidence for "${c.title}" submitted. A manager will review it.`
          : `Evidence for "${c.title}" submitted. That one is done.`);
      } catch { /* a private window; the list still updates */ }
      navigate('/competencies/mine');
    } catch (err) {
      setUploading(null);
      setFailure(isCallFailure(err) ? err.message : err instanceof Error ? err.message : 'The evidence could not be submitted.');
    }
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          <p className={styles.eyebrow}>Evidence · due {formatDay(assignment.dueDate)}</p>
          <div className={styles.titleLine}>
            <BackLink to="/competencies/mine" label="Back to your assignments" />
            <h1 className={styles.title}>{c.title}</h1>
          </div>
          {c.description && <p className={styles.subtitle}>{c.description}</p>}
        </div>
      </header>

      <div className={styles.body}>
        <form className={form.form} onSubmit={(e) => void submit(e)} noValidate style={{ marginTop: 0, maxWidth: 680 }}>
          {failure && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{failure}</div>}

          {req.attestation && (
            <div className={form.field}>
              <p className={form.label}>Attestation</p>
              <p className={own.statement}>{statement}</p>
              <label className={form.check}>
                <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)}
                  aria-invalid={(touched && needAttest) || undefined} aria-describedby={id('att-hint')} />
                <span>I confirm the statement above.</span>
              </label>
              <p className={touched && needAttest ? form.error : form.hint} id={id('att-hint')}>
                {touched && needAttest ? 'Confirm the attestation to continue.' : `Recorded with your name and today's date.`}
              </p>
            </div>
          )}

          {req.fileUpload && (
            <div className={form.field}>
              <label className={form.label} htmlFor={id('files')}>Files</label>
              <FileDrop id={id('files')} multiple onFiles={addFiles} accept={EVIDENCE_ACCEPT} prompt="Choose files" describedBy={id('files-hint')}
                invalid={(touched && needFile) || Boolean(problem)} disabled={busy} />
              <p className={touched && needFile ? form.error : form.hint} id={id('files-hint')}>
                {touched && needFile ? 'Attach at least one file.' : `A certificate, checklist or photo. Documents and images up to 25 MB, ${COMP_FILES_MAX} at most.`}
              </p>
              {problem && <p className={form.error} role="alert">{problem}</p>}
              {files.length > 0 && (
                <ul className={styles.list} style={{ marginTop: 'var(--sp-3)' }}>
                  {files.map((f) => (
                    <li key={`${f.name}-${f.size}`} className={styles.listItem}>
                      <div><div className={styles.itemName}>{f.name}</div><div className={styles.itemMeta}>{formatBytes(f.size)}{uploading === f.name ? ' · uploading…' : ''}</div></div>
                      <button type="button" className={styles.btn} disabled={busy} onClick={() => setFiles(files.filter((x) => x !== f))} aria-label={`Remove ${f.name}`}>Remove</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className={form.field}>
            <label className={form.label} htmlFor={id('notes')}>Notes <span className={form.optional}>(optional)</span></label>
            <textarea id={id('notes')} className={form.textarea} rows={3} maxLength={COMP_TEXT_MAX} value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, COMP_TEXT_MAX))} aria-describedby={id('notes-hint')} />
            <CharCount value={notes} max={COMP_TEXT_MAX} />
            <p className={form.hint} id={id('notes-hint')}>Where and when it was done, who observed it, anything a reviewer should know.</p>
          </div>

          <h2 className={form.group}>Validation record <span className={form.optional}>(optional)</span></h2>
          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('assessor')}>Assessed by</label>
              <input id={id('assessor')} className={form.input} type="text" maxLength={COMP_ASSESSOR_MAX} value={assessorName}
                onChange={(e) => setAssessorName(e.target.value.slice(0, COMP_ASSESSOR_MAX))} autoComplete="off"
                aria-describedby={id('assessor-hint')} />
              <CharCount value={assessorName} max={COMP_ASSESSOR_MAX} />
              <p className={form.hint} id={id('assessor-hint')}>The educator, preceptor or peer who validated it.</p>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('adate')}>Assessment date</label>
              <input id={id('adate')} className={form.input} type="date" value={assessmentDate} onChange={(e) => setAssessmentDate(e.target.value)} />
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('score')}>Score (%)</label>
              <input id={id('score')} className={form.input} type="number" min={0} max={COMP_SCORE_MAX} step={1} value={score}
                onChange={(e) => setScore(e.target.value)} style={{ maxWidth: 140 }} />
            </div>
          </div>

          {req.managerApproval && (
            <div className={`${styles.notice} ${styles.noticeInfo}`} role="note">
              A manager reviews this evidence before the assignment counts as complete.
            </div>
          )}

          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
              {uploading ? `Uploading ${uploading}…` : submitEvidence.isPending ? 'Submitting…' : 'Submit evidence'}
            </button>
            <Link to="/competencies/mine" className={styles.btn}>Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
}
